import { useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiDownload,
  FiEye,
  FiFilter,
  FiPackage,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTruck,
  FiXCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
      }).format(new Date(value))
    : "—";

export default function DeliveryChallanList() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [challans, setChallans] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [storeId, setStoreId] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState("");

  const loadChallans = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest("/delivery-challans", {
        accessToken,
      });

      setChallans(response);
    } catch (requestError) {
      setError(requestError.message || "Unable to load delivery challans.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) loadChallans();
  }, [accessToken]);

  const stores = useMemo(
    () =>
      Array.from(
        new Map(
          challans
            .filter((challan) => challan.store)
            .map((challan) => [challan.store.id, challan.store]),
        ).values(),
      ),
    [challans],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return challans.filter((challan) => {
      const matchesSearch =
        !query ||
        [
          challan.deliveryChallanNumber,
          challan.salesOrder?.salesOrderNumber,
          challan.salesOrder?.customerNameSnapshot,
          challan.store?.name,
          challan.vehicleNumber,
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(query));

      return (
        matchesSearch &&
        (status === "All" || challan.status === status) &&
        (storeId === "All" || challan.storeId === storeId)
      );
    });
  }, [challans, search, status, storeId]);

  const metrics = useMemo(
    () => ({
      total: challans.length,
      draft: challans.filter((item) => item.status === "DRAFT").length,
      dispatched: challans.filter((item) => item.status === "DISPATCHED").length,
      cancelled: challans.filter((item) => item.status === "CANCELLED").length,
    }),
    [challans],
  );

  const dispatch = async (challan) => {
    try {
      setActionId(challan.id);
      await apiRequest(`/delivery-challans/${challan.id}/dispatch`, {
        method: "POST",
        accessToken,
      });
      await loadChallans();
    } catch (requestError) {
      setError(requestError.message || "Unable to dispatch challan.");
    } finally {
      setActionId(null);
    }
  };

  const cancel = async (challan) => {
    const reason = window.prompt("Enter cancellation reason:");
    if (!reason?.trim()) return;

    try {
      setActionId(challan.id);
      await apiRequest(`/delivery-challans/${challan.id}/cancel`, {
        method: "POST",
        accessToken,
        body: { reason: reason.trim() },
      });
      await loadChallans();
    } catch (requestError) {
      setError(requestError.message || "Unable to cancel challan.");
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
        <div>
          <h1 className="text-4xl font-bold">Delivery Challans</h1>
          <p className="text-gray-500 mt-2">
            Manage stock-safe sales-order dispatches.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            disabled
            className="h-11 px-5 rounded-xl border border-gray-200 flex items-center gap-2 opacity-50"
          >
            <FiDownload /> Export
          </button>

          <button
            onClick={() => navigate("/admindashboard/delivery/challan/create")}
            className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2"
          >
            <FiPlus /> Create Delivery Challan
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-6">
        <Stat title="Total Challans" value={metrics.total} icon={<FiPackage />} color="bg-blue-100 text-blue-700" />
        <Stat title="Draft" value={metrics.draft} icon={<FiPackage />} color="bg-gray-100 text-gray-700" />
        <Stat title="Dispatched" value={metrics.dispatched} icon={<FiTruck />} color="bg-green-100 text-green-700" />
        <Stat title="Cancelled" value={metrics.cancelled} icon={<FiXCircle />} color="bg-red-100 text-red-700" />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6">
        <div className="grid lg:grid-cols-4 gap-4">
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search DC, sales order, customer..."
              className="w-full h-12 rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <select value={status} onChange={(event) => setStatus(event.target.value)} className="h-12 rounded-xl border border-gray-200 px-4 bg-white">
            <option value="All">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="DISPATCHED">Dispatched</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <select value={storeId} onChange={(event) => setStoreId(event.target.value)} className="h-12 rounded-xl border border-gray-200 px-4 bg-white">
            <option value="All">All Stores</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="flex justify-between items-center px-6 py-5 border-b">
          <div>
            <h2 className="text-2xl font-bold">Delivery Challans</h2>
            <p className="text-gray-500 mt-1">
              Showing {filtered.length} of {challans.length} challans
            </p>
          </div>

          <button onClick={loadChallans} className="h-11 px-5 rounded-xl border border-gray-200 flex items-center gap-2">
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead className="bg-gray-50">
              <tr>
                <Header>DC No</Header>
                <Header>Date</Header>
                <Header>Sales Order</Header>
                <Header>Customer</Header>
                <Header>Store</Header>
                <Header>Transport</Header>
                <Header align="center">Items</Header>
                <Header align="center">Qty</Header>
                <Header align="center">Status</Header>
                <Header align="center">Actions</Header>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr><td colSpan="10" className="py-16 text-center text-gray-500">Loading challans...</td></tr>
              ) : filtered.length ? (
                filtered.map((challan) => (
                  <tr key={challan.id} className="border-b hover:bg-gray-50">
                    <td className="px-5 py-5 font-semibold">{challan.deliveryChallanNumber}</td>
                    <td className="px-5 py-5">{formatDate(challan.deliveryDate)}</td>
                    <td className="px-5 py-5 text-primary-700 font-medium">{challan.salesOrder?.salesOrderNumber || "—"}</td>
                    <td className="px-5 py-5">{challan.salesOrder?.customerNameSnapshot || "—"}</td>
                    <td className="px-5 py-5">{challan.store?.name || "—"}</td>
                    <td className="px-5 py-5">
                      <p>{challan.transporter || "—"}</p>
                      <p className="text-xs text-gray-500 mt-1">{challan.vehicleNumber || "No vehicle"}</p>
                    </td>
                    <td className="px-5 py-5 text-center">{challan.items?.length || 0}</td>
                    <td className="px-5 py-5 text-center">
                      {(challan.items || []).reduce((sum, item) => sum + Number(item.quantityDispatched || 0), 0)}
                    </td>
                    <td className="px-5 py-5 text-center"><StatusBadge status={challan.status} /></td>
                    <td className="px-5 py-5">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => navigate(`/admindashboard/delivery/challan/${challan.id}`)}
                          className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-primary-700 hover:text-white"
                          title="View"
                        >
                          <FiEye />
                        </button>

                        {challan.status === "DRAFT" && (
                          <>
                            <button
                              disabled={actionId === challan.id}
                              onClick={() => dispatch(challan)}
                              className="h-9 px-3 rounded-lg bg-primary-700 text-white text-xs disabled:opacity-50"
                            >
                              Dispatch
                            </button>

                            <button
                              disabled={actionId === challan.id}
                              onClick={() => cancel(challan)}
                              className="h-9 w-9 rounded-lg border border-red-200 text-red-600 flex items-center justify-center"
                              title="Cancel"
                            >
                              <FiXCircle />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="10" className="py-16 text-center text-gray-500">No delivery challans found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Header({ children, align = "left" }) {
  return <th className={`px-5 py-4 text-${align} text-xs font-semibold uppercase text-gray-500`}>{children}</th>;
}

function Stat({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 flex justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <h2 className="text-3xl font-bold mt-3">{value}</h2>
      </div>
      <div className={`h-14 w-14 rounded-2xl flex items-center justify-center text-2xl ${color}`}>{icon}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    DRAFT: "bg-gray-100 text-gray-700",
    DISPATCHED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}